// Client-side invoice PDF generation via browser print dialog
// Produces a properly formatted A4 invoice with GST breakdown
//
// `signatureUrl` (drawn) and `sealUrl` (uploaded) come from the admin profile
// rather than the bill — so updating either changes every invoice, past and
// future. `sealUrl` falls back to the seal bundled with the app.

const DEFAULT_SEAL = "/seals/HILANDSEAL.png";

export function generateInvoicePDF(bill, { signatureUrl, sealUrl } = {}) {
  const isPaid = bill.status === "paid";
  const hasGST = bill.gstType !== "none";
  const seal = sealUrl || DEFAULT_SEAL;

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—";

  const statusBadge = {
    paid: { text: "PAID", bg: "#064e3b", color: "#10b981", border: "#065f46" },
    sent: { text: "SENT", bg: "#1e3a5f", color: "#60a5fa", border: "#1e40af" },
    draft: { text: "DRAFT", bg: "#374151", color: "#9ca3af", border: "#4b5563" },
    overdue: { text: "OVERDUE", bg: "#4a1942", color: "#f87171", border: "#7f1d1d" },
    cancelled: { text: "CANCELLED", bg: "#1f2937", color: "#6b7280", border: "#374151" },
  }[bill.status] || { text: bill.status?.toUpperCase(), bg: "#1f2937", color: "#9ca3af", border: "#374151" };

  const itemRows = (bill.items || []).map((item, i) => `
    <tr style="border-bottom:1px solid #e5e7eb;">
      <td style="padding:10px 8px;color:#111827;font-size:13px;">${i + 1}</td>
      <td style="padding:10px 8px;color:#111827;font-size:13px;">${item.date}</td>
      <td style="padding:10px 8px;color:#111827;font-size:13px;">${item.description}${item.hsnCode ? `<br><span style="color:#9ca3af;font-size:11px;">HSN/SAC: ${item.hsnCode}</span>` : ""}</td>
      <td style="padding:10px 8px;text-align:center;color:#374151;font-size:13px;">${item.quantity} ${item.unit || ""}</td>
      <td style="padding:10px 8px;text-align:right;color:#374151;font-size:13px;">${formatCurrency(item.rate)}</td>
      <td style="padding:10px 8px;text-align:right;color:#111827;font-weight:600;font-size:13px;">${formatCurrency(item.amount)}</td>
    </tr>
  `).join("");

  const gstRows = hasGST ? (
    bill.gstType === "cgst_sgst" ? `
      <tr><td colspan="4" style="text-align:right;padding:6px 8px;color:#6b7280;font-size:13px;">CGST @ ${bill.gstRate / 2}%</td><td style="text-align:right;padding:6px 8px;font-size:13px;">${formatCurrency(bill.cgst)}</td></tr>
      <tr><td colspan="4" style="text-align:right;padding:6px 8px;color:#6b7280;font-size:13px;">SGST @ ${bill.gstRate / 2}%</td><td style="text-align:right;padding:6px 8px;font-size:13px;">${formatCurrency(bill.sgst)}</td></tr>
    ` : `
      <tr><td colspan="4" style="text-align:right;padding:6px 8px;color:#6b7280;font-size:13px;">IGST @ ${bill.gstRate}%</td><td style="text-align:right;padding:6px 8px;font-size:13px;">${formatCurrency(bill.igst)}</td></tr>
    `
  ) : "";

  const bankDetailsBox = bill.companyBankDetails ? `
  <div style="margin-top:20px;border:1px solid #bfdbfe;border-radius:8px;padding:16px;background:#eff6ff;">
    <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#3b82f6;margin-bottom:10px;">Bank Details</div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:8px;">
      ${bill.companyBankDetails.bankName ? `<div><span style="font-size:11px;color:#6b7280;">Bank</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.bankName}</span></div>` : ""}
      ${bill.companyBankDetails.accountName ? `<div><span style="font-size:11px;color:#6b7280;">Account Name</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.accountName}</span></div>` : ""}
      ${bill.companyBankDetails.accountNumber ? `<div><span style="font-size:11px;color:#6b7280;">Account No.</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.accountNumber}</span></div>` : ""}
      ${bill.companyBankDetails.ifscCode ? `<div><span style="font-size:11px;color:#6b7280;">IFSC Code</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.ifscCode}</span></div>` : ""}
      ${bill.companyBankDetails.branch ? `<div><span style="font-size:11px;color:#6b7280;">Branch</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.branch}</span></div>` : ""}
      ${bill.companyBankDetails.upiId ? `<div><span style="font-size:11px;color:#6b7280;">UPI ID</span><br><span style="font-size:13px;font-weight:600;color:#1e40af;">${bill.companyBankDetails.upiId}</span></div>` : ""}
    </div>
  </div>
  ` : "";

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Invoice ${bill.billNumber}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#111827; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display:none; }
  }
  .invoice { max-width:800px; margin:0 auto; padding:40px; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:32px; padding-bottom:24px; border-bottom:2px solid #1e40af; }
  .company-name { font-size:24px; font-weight:800; color:#1e40af; letter-spacing:-0.5px; }
  .company-details { font-size:12px; color:#6b7280; margin-top:4px; line-height:1.6; }
  .invoice-meta { text-align:right; }
  .invoice-title { font-size:28px; font-weight:300; color:#374151; letter-spacing:4px; text-transform:uppercase; }
  .invoice-num { font-size:13px; color:#6b7280; margin-top:4px; }
  .invoice-num strong { color:#111827; }
  .status-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:700; letter-spacing:1px; margin-top:8px; }
  .addresses { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-bottom:28px; }
  .address-block { background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:16px; }
  .address-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#9ca3af; margin-bottom:8px; }
  .address-name { font-size:14px; font-weight:700; color:#111827; }
  .address-text { font-size:12px; color:#6b7280; line-height:1.6; margin-top:2px; }
  .dates-row { display:flex; gap:24px; margin-bottom:28px; }
  .date-item { background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; padding:12px 16px; flex:1; }
  .date-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#3b82f6; }
  .date-value { font-size:13px; font-weight:600; color:#1e40af; margin-top:4px; }
  table { width:100%; border-collapse:collapse; margin-bottom:16px; }
  thead tr { background:#1e40af; color:white; }
  thead th { padding:11px 8px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; }
  thead th:last-child, thead th:nth-child(3), thead th:nth-child(4) { text-align:right; }
  tbody tr:nth-child(even) { background:#f9fafb; }
  .totals-section { display:flex; justify-content:flex-end; margin-bottom:28px; }
  .totals-table { width:320px; }
  .totals-table td { padding:5px 8px; font-size:13px; }
  .totals-table .divider td { border-top:1px solid #e5e7eb; padding-top:10px; }
  .grand-total { background:#1e40af; color:white; }
  .grand-total td { padding:12px 8px !important; font-weight:700; font-size:15px; }
  .paid-stamp { text-align:center; margin:20px 0; }
  .paid-box { display:inline-block; border:3px solid #10b981; color:#10b981; padding:10px 40px; font-size:28px; font-weight:900; letter-spacing:8px; transform:rotate(-3deg); opacity:0.85; border-radius:4px; }
  .footer { margin-top:28px; padding-top:20px; border-top:1px solid #e5e7eb; display:flex; justify-content:space-between; align-items:flex-start; }
  .notes-section { max-width:400px; }
  .notes-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#9ca3af; margin-bottom:4px; }
  .notes-text { font-size:12px; color:#6b7280; line-height:1.6; }
  /* signature sits flush against the seal's left edge — no gap between them,
     the pair right-aligned above the company rule, seal keeping its place */
  .stamp-row { display:flex; align-items:center; justify-content:flex-end; gap:0; margin-bottom:6px; }
  .seal { height:90px; width:auto; object-fit:contain; display:block; flex-shrink:0; }
  .signature { height:66px; max-width:200px; width:auto; object-fit:contain; display:block; flex-shrink:0; }
  .print-btn { position:fixed; top:20px; right:20px; background:#1e40af; color:white; border:none; padding:10px 24px; border-radius:8px; font-size:14px; font-weight:600; cursor:pointer; z-index:9999; }
</style>
</head>
<body>
<button class="no-print print-btn" onclick="window.print()">🖨️ Print / Save PDF</button>
<div class="invoice">
  <!-- Header -->
  <div class="header">
    <div>
      <img src="/logo.png" alt="Logo" style="height:56px;width:auto;object-fit:contain;" onerror="this.style.display='none'" />
      <div class="company-details">
        ${bill.companyAddress ? bill.companyAddress + "<br>" : ""}
        ${bill.companyGST ? "GSTIN: " + bill.companyGST + "<br>" : ""}
        ${bill.companyEmail || ""} ${bill.companyPhone ? "| " + bill.companyPhone : ""}
      </div>
    </div>
    <div class="invoice-meta">
      <div class="invoice-title">Invoice</div>
      <div class="invoice-num">Bill No: <strong>${bill.billNumber}</strong></div>
      <div><span class="status-badge" style="background:${statusBadge.bg};color:${statusBadge.color};border:1px solid ${statusBadge.border};">${statusBadge.text}</span></div>
    </div>
  </div>

  <!-- Addresses -->
  <div class="addresses">
    <div class="address-block">
      <div class="address-label">Bill To</div>
      <div class="address-name">${bill.clientId?.name || bill.clientId || "—"}</div>
      <div class="address-text">
        ${bill.clientId?.company ? bill.clientId.company + "<br>" : ""}
        ${bill.clientId?.email || ""} ${bill.clientId?.phone ? "| " + bill.clientId.phone : ""}
        ${bill.clientId?.gstin ? "<br>GSTIN: " + bill.clientId.gstin : ""}
      </div>
    </div>
    <div class="address-block">
      <div class="address-label">${bill.siteId ? "Site / Project" : "Invoice Details"}</div>
      <div class="address-name">${bill.siteId?.name || (bill.subject || "—")}</div>
      <div class="address-text">${bill.siteId?.code ? "Code: " + bill.siteId.code : ""}${bill.description ? "<br>" + bill.description : ""}</div>
    </div>
  </div>

  <!-- Dates -->
  <div class="dates-row">
    <div class="date-item">
      <div class="date-label">Invoice Date</div>
      <div class="date-value">${fmt(bill.billDate)}</div>
    </div>
    ${bill.paidDate ? `<div class="date-item" style="background:#ecfdf5;border-color:#6ee7b7;"><div class="date-label" style="color:#059669;">Paid On</div><div class="date-value" style="color:#047857;">${fmt(bill.paidDate)}</div></div>` : ""}
  </div>

  <!-- Items table -->
  <table>
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th>Date</th>
        <th style="width:90px;text-align:center;">Description</th>
        <th style="width:90px;text-align:center;">Qty</th>
        <th style="width:110px;text-align:right;">Rate</th>
        <th style="width:110px;text-align:right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <!-- Totals -->
  <div class="totals-section">
    <table class="totals-table">
      <tbody>
        <tr><td style="color:#6b7280;">Sub Total</td><td style="text-align:right;">${formatCurrency(bill.subTotal)}</td></tr>
        ${bill.discount > 0 ? `<tr><td style="color:#6b7280;">Discount</td><td style="text-align:right;color:#dc2626;">- ${formatCurrency(bill.discount)}</td></tr><tr><td style="color:#6b7280;">Taxable Amount</td><td style="text-align:right;">${formatCurrency(bill.taxableAmount)}</td></tr>` : ""}
        ${gstRows}
        <tr class="divider">
          <td colspan="2"></td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="grand-total">
          <td>Grand Total</td>
          <td style="text-align:right;">${formatCurrency(bill.grandTotal)}</td>
        </tr>
        ${isPaid ? `<tr><td style="color:#6b7280;padding-top:6px;">Paid Amount</td><td style="text-align:right;color:#10b981;padding-top:6px;font-weight:700;">${formatCurrency(bill.paidAmount)}</td></tr>` : ""}
        ${bill.paymentMode ? `<tr><td style="color:#9ca3af;font-size:12px;">Mode</td><td style="text-align:right;color:#6b7280;font-size:12px;">${bill.paymentMode}${bill.paymentRef ? " · " + bill.paymentRef : ""}</td></tr>` : ""}
      </tfoot>
    </table>
  </div>

  ${isPaid ? `<div class="paid-stamp"><div class="paid-box">PAID</div></div>` : ""}

  <!-- Footer -->
  <div class="footer">
    <div class="notes-section">
      ${bill.notes ? `<div class="notes-label">Notes</div><div class="notes-text">${bill.notes}</div>` : ""}
    </div>
    <div style="text-align:right;">
      <div style="font-size:11px;color:#9ca3af;margin-bottom:6px;">Authorised Signature</div>
      <div class="stamp-row">
        ${signatureUrl ? `<img src="${signatureUrl}" alt="Signature" class="signature" onerror="this.style.display='none'" />` : ""}
        <img src="${seal}" alt="Seal" class="seal" onerror="this.style.display='none'" />
      </div>
      <div style="border-top:1px solid #374151;padding-top:6px;font-size:12px;color:#6b7280;">${bill.companyName || ""}</div>
    </div>
  </div>

  <!-- Bank Details -->
  ${hasGST ? bankDetailsBox : ""}

  <div style="margin-top:24px;text-align:center;font-size:11px;color:#d1d5db;border-top:1px solid #f3f4f6;padding-top:16px;">
    This is a computer-generated invoice · Generated by Hiland Survey ERP
  </div>
</div>
<script>
  // Print only once every image has settled. The signature is fetched from
  // Firebase, so a fixed delay would sometimes print an invoice with the
  // signature block still blank. The 5s ceiling keeps a dead image from
  // stalling the dialog forever.
  (function () {
    var settled = function (img) {
      return new Promise(function (resolve) {
        if (img.complete) return resolve();
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    };
    var images = Array.prototype.slice.call(document.images).map(settled);
    var ceiling = new Promise(function (resolve) { setTimeout(resolve, 5000); });

    Promise.race([Promise.all(images), ceiling]).then(function () {
      setTimeout(function () { window.print(); }, 150);
    });
  })();
</script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return;                       // pop-up blocked
  w.document.write(html);
  w.document.close();
}